<template>
    <div class="p-3">
        <h1>{{$t ("headers.calendar") }}</h1>

        <FullCalendar id="calendar" ref="calendar" :options="calendarOptions" />
    </div>

    <!-- Modal for VehicleEntry event -->
    <teleport to="body">
        <div class="modal fade" id="vehicleModal" tabindex="-1" aria-hidden="true" ref="vehicleModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content modal_wrapper">
                    <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                        <h3 class="modal-title">{{ $t("headers.vehicleDetail") }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="selectedEvent">
                            <p><strong>{{ $t("vehicleEntry.carrier") }}:</strong> {{ selectedEvent.carrier }}</p>
                            <p><strong>{{ $t("vehicleEntry.driver_name") }}:</strong> {{ selectedEvent.driver_name }}</p>
                            <p><strong>{{ $t("vehicleEntry.licencePlate") }}:</strong> {{ selectedEvent.licence_plate }}</p>
                            <p>
                                <strong>{{ $t("vehicleEntry.from") }}:</strong>
                                {{ selectedEvent.expected_from ? $d(new Date(selectedEvent.expected_from), 'short') : "—" }}
                            </p>
                            <p>
                                <strong>{{ $t("vehicleEntry.to") }}:</strong>
                                {{ selectedEvent.expected_to ? $d(new Date(selectedEvent.expected_to), 'short') : "—" }}
                            </p>
                            <StatusSelector
                                :record="selectedEvent"
                                statusType="vehicleEntry"
                                modalId="vehicleModal"
                                :getUrl="`/${$route.params.slug}/statuses?model=vehicleentry`"
                                :submitUrl="`/${$route.params.slug}/vehicle-entries/${selectedEvent.id}/status/edit`"
                                @status-updated="updateCalendarEvent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>

    <!-- Modal for Guestbook event -->
    <teleport to="body">
        <div class="modal fade" id="guestbookModal" tabindex="-1" aria-hidden="true" ref="guestbookModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content modal_wrapper">
                    <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                        <h3 class="modal-title">{{ $t("headers.guestbookDetail") }}</h3>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="selectedEvent">
                            <p><strong>{{ $t("guestbook.name") }}:</strong> {{ selectedEvent.guest_name }}</p>
                            <p><strong>{{ $t("guestbook.company") }}:</strong> {{ selectedEvent.company }}</p>
                            <p><strong>{{ $t("guestbook.cardNumber") }}:</strong> {{ selectedEvent.personal_card_number }}</p>
                            <p><strong>{{ $t("guestbook.cardType") }}:</strong> {{ selectedEvent.personal_card_type ? $t("documentType." + selectedEvent.personal_card_type) : "-" }}</p>
                            <p>
                                <strong>{{ $t("guestbook.from") }}:</strong>
                                {{ selectedEvent.expected_from ? $d(new Date(selectedEvent.expected_from), 'short') : "—" }}
                            </p>
                            <p>
                                <strong>{{ $t("guestbook.to") }}:</strong>
                                {{ selectedEvent.expected_to ? $d(new Date(selectedEvent.expected_to), 'short') : "-" }}
                            </p>
                            <StatusSelector
                                :record="selectedEvent"
                                statusType="guestbook"
                                modalId="guestbookModal"
                                :getUrl="`/${$route.params.slug}/statuses?model=guestbook`"
                                :submitUrl="`/${$route.params.slug}/guestbooks/${selectedEvent.id}/status/edit`"
                                @status-updated="updateCalendarEvent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
    import FullCalendar from "@fullcalendar/vue3";
    import dayGridPlugin from "@fullcalendar/daygrid";
    import interactionPlugin from "@fullcalendar/interaction";
    import timeGridPlugin from "@fullcalendar/timegrid";
    import multiMonthPlugin from "@fullcalendar/multimonth";
    import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
    import StatusSelector from "@/components/StatusSelector.vue";

    export default {
        components: {
            FullCalendar,
            StatusSelector,
        },
        data() {
            return {
                selectedEvent: null,
                vehicleModal: null,
                guestbookModal: null,
                calendarOptions: {
                    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, multiMonthPlugin],
                    initialView: "timeGridDay",
                    height: "auto",
                    locale: "cs",
                    firstDay: "1",
                    selectable: true,
                    editable: false,
                    nowIndicator: true,
                    headerToolbar: {
                        start: "today prev,next",
                        center: "title",
                        right: "timeGridDay timeGridWeek dayGridMonth multiMonthYear",
                    },
                    buttonText: {
                        today: "Dnes",
                        day: "Den",
                        week: "Týden",
                        month: "Měsíc",
                        year: "Rok",
                    },
                    allDayText: "",
                    views: {
                        timeGridWeek: {
                            slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                        },
                        timeGridDay: {
                            slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false }
                        }
                    },
                    dayHeaderFormat: { weekday: "short" },
                    dayHeaderContent: (args) => {
                        const date = args.date;
                        const dayName = args.text.charAt(0).toUpperCase() + args.text.slice(1);
                        const dayNumber = date.getDate();

                        return {
                            html: `<div>${dayName}</div><div style="font-size: 1.3em; ">${dayNumber}</div>`
                        };
                    },
                    eventClick:this.handleEventClick,
                    events: [],
                }
            }
        },
        created() {
            this.getCalendarEvents();
        },
        mounted() {
            this.vehicleModal = new Modal(this.$refs.vehicleModal, { backdrop: "static"});
            this.guestbookModal = new Modal(this.$refs.guestbookModal, { backdrop: "static"});
        },
        methods: {
            getCalendarEvents() {
                const slug = this.$route.params.slug

                Promise.all([
                    this.$store.getters.api.get(`${slug}/vehicle-entries/calendar`),
                    this.$store.getters.api.get(`${slug}/guestbooks/calendar`)
                ])
                .then(res => {
                    const vehicleRes = res[0];
                    const guestbookRes = res[1];

                    this.calendarOptions.events = [
                        ...vehicleRes.data,
                        ...guestbookRes.data
                    ]
                })
                .catch(error => {
                    console.error("Error loading calendar events", error);
                });
            },

            handleEventClick(info) {
                const event = info.event;
                const type = event.extendedProps.type;

                this.selectedEvent = {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end,
                    ...event.extendedProps
                };
                this.openModal(type)
            },
            openModal(type) {
                if (type === "vehicle") {
                    this.vehicleModal.show()
                } else if (type === "guestbook") {
                    this.guestbookModal.show()
                }
            },
            updateCalendarEvent() {
                this.getCalendarEvents();
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import "@/scss/abstracts/variables";

    #calendar :deep(.fc-toolbar-title) {
        color: $black;
        font-weight: bold;
        text-transform: capitalize;
    }

    #calendar :deep(.fc-timegrid-slot) {
        color: $black;
    }

    :deep(.fc-daygrid-day-top) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    :deep(.fc-daygrid-day-number) {
        color: $black;
        text-decoration: none;
    }
    :deep(.fc-col-header-cell-cushion) {
        text-decoration: none;
        color: $black;
    }
    :deep(.fc-timegrid-slot.fc-timegrid-slot-label.fc-timegrid-slot-minor) {
        border-top: none;
    }
    :deep(.fc-timegrid-slot.fc-timegrid-slot-lane.fc-timegrid-slot-minor) {
        border-top: none;
    }
</style>